import React from 'react';
import { MapPin, ShoppingBag, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';
import { SmartImage } from '../../../../components/common/SmartImage';

interface LocalOffer {
  name: string;
  productName?: string;
  storeName: string;
  storeAddress?: string;
  price: number;
  distance?: number | null;
  storeLogoUrl?: string | null;
  pricePerUnit?: number | null;
  unit?: string | null;
  imageUrl?: string | null;
  sourceUrl?: string | null;
}

interface LocalOffersCardProps {
  itemName?: string;
  itemPrice?: number;
  userLocationAddress?: string;
  deals: LocalOffer[];
  currencySymbol?: string;
}

export const LocalOffersCard: React.FC<{ props: LocalOffersCardProps; isStreaming?: boolean }> = ({ props, isStreaming }) => {
  const { t } = useTranslation();
  const { userProfile, currency } = useStore();
  const { itemName, itemPrice, userLocationAddress, deals = [], currencySymbol: propCurrencySymbol } = props || {};

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find((c) => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, currency, propCurrencySymbol]);

  if (!itemName) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{t('basket.grounding_loading', { defaultValue: 'Finding Local Prices...' })}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-6 pt-6 pb-4 border-b border-gray-50">
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
          <span className="text-[9px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full tracking-widest inline-block">
            {t('basket.grounding_deals_badge', { defaultValue: 'Local Offers' })}
          </span>
          {userLocationAddress && (
            <span className="text-[9px] font-bold text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full flex items-center gap-1">
              <MapPin size={8} className="text-gray-400" />
              {userLocationAddress}
            </span>
          )}
        </div>
        <h3 className="text-sm font-black text-[#1a2b3c] leading-tight truncate">{itemName}</h3>
      </div>

      <div className="p-4 space-y-3 max-h-96 overflow-y-auto scrollbar-hide">
        {deals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-2">
              <ShoppingBag size={22} />
            </div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              {t('basket.no_deals_available', { defaultValue: 'No comparison deals found.' })}
            </p>
          </div>
        ) : (
          deals.map((deal, idx) => {
            const priceDifference = itemPrice != null ? itemPrice - deal.price : null;
            let diffBadge: React.ReactNode = null;
            if (priceDifference != null) {
              if (priceDifference > 0) {
                diffBadge = (
                  <span className="text-[8px] font-black uppercase text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-1.5 py-0.5 rounded-full shrink-0">
                    {t('common.save')} {activeCurrencySymbol}{priceDifference.toFixed(2)}
                  </span>
                );
              } else if (priceDifference < 0) {
                diffBadge = (
                  <span className="text-[8px] font-black uppercase text-rose-600 bg-rose-50 border border-rose-100/50 px-1.5 py-0.5 rounded-full shrink-0">
                    +{activeCurrencySymbol}{Math.abs(priceDifference).toFixed(2)}
                  </span>
                );
              }
            }

            const content = (
              <div className="group bg-gray-50/50 border border-gray-100 rounded-2xl p-3 flex items-start gap-3 transition-all">
                <div className="w-12 h-12 bg-white border border-gray-200/50 rounded-xl overflow-hidden shrink-0 flex items-center justify-center">
                  {deal.imageUrl ? (
                    <SmartImage imageUrl={deal.imageUrl} className="w-full h-full object-cover" />
                  ) : (
                    <ShoppingBag size={16} className="text-gray-300" />
                  )}
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-black text-[#1a2b3c] min-w-0">
                      <div className="w-4 h-4 rounded-full bg-white border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                        {deal.storeLogoUrl ? (
                          <SmartImage imageUrl={deal.storeLogoUrl} className="w-full h-full object-contain" />
                        ) : (
                          <ShoppingBag size={9} className="text-indigo-500" />
                        )}
                      </div>
                      <span className="truncate">{deal.storeName}</span>
                    </div>
                    {deal.distance != null && (
                      <span className="text-[8px] font-black uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full shrink-0">
                        {deal.distance.toFixed(1)} {t('basket.mi_unit', { defaultValue: 'mi' })}
                      </span>
                    )}
                  </div>

                  {(deal.name || deal.productName) && (
                    <p className="text-[10px] font-bold text-gray-500 leading-snug line-clamp-2" title={deal.name || deal.productName}>
                      {deal.name || deal.productName}
                    </p>
                  )}

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-black text-[#1a2b3c]">
                        {activeCurrencySymbol}
                        {deal.price.toFixed(2)}
                      </span>
                      {deal.pricePerUnit != null && deal.unit && (
                        <span className="text-[8px] font-bold text-gray-400">
                          ({activeCurrencySymbol}{deal.pricePerUnit.toFixed(2)}/{deal.unit})
                        </span>
                      )}
                      {diffBadge}
                    </div>
                    {deal.storeAddress && (
                      <span className="text-[9px] text-gray-400 font-bold truncate max-w-[100px] flex items-center gap-0.5" title={deal.storeAddress}>
                        <MapPin size={9} className="text-gray-300 shrink-0" />
                        {deal.storeAddress.split(',')[0]}
                      </span>
                    )}
                  </div>
                </div>

                {deal.sourceUrl && (
                  <div className="flex items-center justify-center p-1.5 bg-white border border-gray-100 text-gray-400 group-hover:text-indigo-600 group-hover:border-indigo-100 rounded-xl transition-all shrink-0 self-center">
                    <ExternalLink size={12} />
                  </div>
                )}
              </div>
            );

            return deal.sourceUrl ? (
              <a key={idx} href={deal.sourceUrl} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
                {content}
              </a>
            ) : (
              <div key={idx}>{content}</div>
            );
          })
        )}
      </div>
    </div>
  );
};
